<script setup lang="ts">
import { Button, ZhEmptyState } from '@zerohuman/ui'
import { RouterLink } from 'vue-router'

/**
 * The empty state for a page that has genuinely never had anything on it — nothing bound, nothing ever
 * run — as opposed to a search or filter that narrowed real rows to zero, where a bare "No rows." is the
 * honest answer. It says what will appear there, in the page's own words, and, when the page asks for one,
 * offers the one action that fills it.
 *
 * Drawn by the kit's `ZhEmptyState`, so every page's nothing-here looks the same and states no border or
 * radius of its own. Several pages render it (home, runs, gates, plans, executions), so it adds no copy of
 * its own: with no `label` it shows the lead and no kicker, exactly the words each page already ships. The
 * action goes through the kit's own action slot as a kit `Button` router link; an action with no route is a kit
 * `Button` that tells the page it was pressed (`act`), for a page whose action opens something in place (#2131).
 * With no action there is no slot.
 */
withDefaults(
    defineProps<{
        /** What is empty, in the page's own words. */
        lead?: string
        /** An optional mono kicker above it. None unless a page asks for one. */
        label?: string
        /** The one action that fills the page: a label and the route it goes to, or no route to act in place. */
        action?: { label: string; to?: string }
    }>(),
    { lead: 'Nothing has run yet.' },
)

const emit = defineEmits<{ act: [] }>()
</script>

<template>
	<ZhEmptyState data-testid="first-run-hint" :label="label">
		{{ lead }}
		<template v-if="action" #action>
			<Button v-if="action.to" as-child size="sm">
				<RouterLink :to="action.to">{{ action.label }}</RouterLink>
			</Button>
			<Button v-else type="button" size="sm" @click="emit('act')">{{ action.label }}</Button>
		</template>
	</ZhEmptyState>
</template>
